import AddCircleIcon from '@mui/icons-material/AddCircle';
import { FormGroup, FormLabel, Grid, Modal, Stack, TextField, Tooltip } from '@mui/material';

import Button from '@mui/material/Button';

import { useEffect, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';

import { useQuery } from 'react-query';

import { useParams } from 'react-router';

import * as grafanaFolderApi from '../../api/app/grafanaFolderApi';

import * as groupApi from '../../api/app/groupApi';

import { ApiError } from '../../components/core/ApiError/ApiError';

import { CheckBox } from '../../components/core/CheckBox/CheckBox';

import { Loader } from '../../components/core/Loader/Loader';

import { SelectBox } from '../../components/core/SelectBox/SelectBox';

import { SelectBoxAutocomplete } from '../../components/core/SelectBox/SelectBoxAutocomplete';

import { useTranslation } from '../../contexts/translation';

import { AssetInputs, assetTypes } from '../../types/app/asset.types';

import { GrafanaFolder } from '../../types/app/grafanaFolder.types';

import { Group } from '../../types/app/group.types';

import { dictionary } from '../../utils/core/dictionary';

import { GrafanaCreateFolder } from './GrafanaCreateFolder';

interface Props {
  onSubmit: SubmitHandler<AssetInputs>;
  defaultValues?: AssetInputs;
  onBack: any;
  isUsedHydra: boolean;
  isReadOnly: boolean;
}

export function AssetForm({ onSubmit, defaultValues, onBack, isUsedHydra, isReadOnly }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [addFolderActive, setAddFolderActive] = useState<boolean>(true);
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<AssetInputs>();

  const {
    data: groupList,
    isLoading,
    isError,
  } = useQuery<Group[]>('groupList', groupApi.getGroupList);

  const {
    data: uidFolderList,
    isLoading: uidListIsLoading,
    isError: uidListIsError,
  } = useQuery<GrafanaFolder[]>('uIdFolderList', grafanaFolderApi.getList);

  useEffect(() => {
    if (!defaultValues) return;
    reset({ ...defaultValues });
    setAddFolderActive(
      defaultValues.grafanaFolderUId &&
        uidFolderList?.find((x) => x.uid === defaultValues.grafanaFolderUId)
        ? false
        : true,
    );

    if (
      defaultValues.grafanaFolderUId &&
      uidFolderList?.some((x) => x.uid === defaultValues.grafanaFolderUId) === false
    ) {
      uidFolderList.push({
        id: 0,
        title: defaultValues.grafanaFolderUId,
        uid: defaultValues.grafanaFolderUId,
      } as GrafanaFolder);
    }
  }, [defaultValues, uidFolderList]);

  if (isError || uidListIsError) return <ApiError />;
  if (isLoading || uidListIsLoading || !groupList || !uidFolderList) return <Loader />;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <SelectBox
              isRequired
              label={t('page.asset.edit-input-label-1')}
              items={assetTypes}
              valueKey={'code'}
              labelKey={'name'}
              controllerProps={{
                name: 'assetType',
                control: control,
                rules: { required: t('page.asset.edit-input-label-1-required') },
                defaultValue: defaultValues?.assetType
                  ? defaultValues?.assetType
                  : assetTypes[0].code,
              }}
              errors={errors}
              isReadOnly={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label={t('page.asset.edit-input-label-2')}
              {...register('name', { required: t('page.asset.edit-input-label-2-required') })}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
              autoFocus
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label={t('page.asset.edit-input-label-3')}
              {...register('influxMachineName', {
                required: t('page.asset.edit-input-label-3-required'),
              })}
              error={Boolean(errors.influxMachineName)}
              helperText={errors.influxMachineName?.message}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectBoxAutocomplete
              label={t('page.asset.edit-input-label-4')}
              items={groupList}
              valueKey={'id'}
              labelKey={'name'}
              controllerProps={{
                name: 'groupId',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>
          {id && (
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={10}>
                  <SelectBoxAutocomplete
                    label={t('page.asset.edit-input-label-5')}
                    items={uidFolderList}
                    valueKey={'uid'}
                    labelKey={'title'}
                    controllerProps={{
                      name: 'grafanaFolderUId',
                      control: control,
                    }}
                    errors={errors}
                    readOnly={isReadOnly}
                  />
                </Grid>
                <Tooltip title={t('page.asset.edit-input-label-5-tooltip')}>
                  <Grid item xs={2}>
                    <Button
                      style={{ marginTop: 10 }}
                      onClick={() => setOpen(true)}
                      disabled={!addFolderActive || isReadOnly}
                    >
                      <AddCircleIcon />
                    </Button>
                  </Grid>
                </Tooltip>
              </Grid>
            </Grid>
          )}
          {/* {isUsedHydra && (
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label={dictionary.HYDRA_MACHINE_NUMBER}
                {...register('hydraMachineNumber', {
                  maxLength: 50,
                })}
                error={Boolean(errors.hydraMachineNumber)}
                helperText={errors.hydraMachineNumber?.message}
                disabled={isReadOnly}
              />
            </Grid>
          )} */}
          {isUsedHydra && (
            <Grid
              item
              xs={12}
              sx={{ border: '1px solid #858585', borderRadius: '5px', margin: 1, padding: 1 }}
            >
              <FormLabel component="legend" style={{ marginBottom: 10 }}>
                {dictionary.HYDRA_CHECKBOX_OPTIONS}
              </FormLabel>
              <FormGroup>
                <TextField
                  required
                  fullWidth
                  label={dictionary.HYDRA_MACHINE_NUMBER}
                  {...register('hydraMachineNumber', {
                    maxLength: 50,
                  })}
                  error={Boolean(errors.hydraMachineNumber)}
                  helperText={errors.hydraMachineNumber?.message}
                  disabled={isReadOnly}
                />
                <CheckBox
                  label={dictionary.HYDRA_LOAD_PLAN}
                  controllerProps={{
                    name: 'loadPlanFromHydra',
                    control: control,
                  }}
                  errors={errors}
                  readOnly={isReadOnly}
                />
                <CheckBox
                  label={dictionary.HYDRA_LOAD_PLAN_MULTIPLICITY}
                  controllerProps={{
                    name: 'loadPlanFromHydraMultiplicity',
                    control: control,
                  }}
                  errors={errors}
                  readOnly={isReadOnly}
                />
                <TextField
                  type={'number'}
                  fullWidth
                  label={dictionary.HYDRA_LOAD_PLAN_MULTIPLE}
                  {...register('loadPlanFromHydraMultiple', {
                    maxLength: 50,
                  })}
                  error={Boolean(errors.hydraMachineNumber)}
                  helperText={errors.hydraMachineNumber?.message}
                  disabled={isReadOnly}
                />
              </FormGroup>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              label={t('page.asset.edit-input-label-unitOverride')}
              {...register('unitOverride', {
                maxLength: {
                  value: 10,
                  message: `${t('general.max-length-is')} 10`,
                },
              })}
              error={Boolean(errors.unitOverride)}
              helperText={errors.unitOverride?.message}
              disabled={isReadOnly}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckBox
              label={t('page.asset.edit-input-label-6')}
              controllerProps={{
                name: 'useWithDashboards',
                control: control,
              }}
              errors={errors}
              readOnly={isReadOnly}
            />
          </Grid>
          <Grid item>
            <Stack spacing={2} direction="row">
              <Button type="button" variant="outlined" onClick={onBack}>
                {t('general.action-buttons.back')}
              </Button>
              {!isReadOnly && (
                <Button type="submit" variant="contained" color="success">
                  {t('general.action-buttons.save')}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </form>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div>
          <GrafanaCreateFolder
            defaultValues={defaultValues}
            setOpen={setOpen}
            assetId={Number(id)}
          />
        </div>
      </Modal>
    </>
  );
}
